import React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { driverService } from '../../services/driver.service';
import { Driver } from '../../types';
import { Modal } from '../../components/ui/Modal';
import { Input } from '../../components/ui/Input';
import { Button } from '../../components/ui/Button';
import toast from 'react-hot-toast';

const driverSchema = z.object({
  fullName: z.string().min(1, 'Full name is required'),
  licenseNumber: z.string().min(1, 'License number is required'),
  phoneNumber: z.string().min(1, 'Phone number is required'),
  email: z.string().optional().refine((val) => !val || val === '' || z.string().email().safeParse(val).success, {
    message: 'Invalid email',
  }),
});

type DriverFormData = z.infer<typeof driverSchema>;

interface DriverFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  driver: Driver | null;
}

export const DriverFormModal: React.FC<DriverFormModalProps> = ({ isOpen, onClose, driver }) => {
  const queryClient = useQueryClient();
  const isEdit = !!driver;

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<DriverFormData>({
    resolver: zodResolver(driverSchema),
    defaultValues: driver || undefined,
  });

  const createMutation = useMutation({
    mutationFn: driverService.create,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['drivers'] });
      toast.success(
        `Driver registered successfully!\nLogin credentials:\n- Username: ${data.licenseNumber}\n- Password: Transport@2026\n- User must change password on first login`,
        { duration: 8000 }
      );
      reset();
      onClose();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to create driver');
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data: DriverFormData) => driverService.update(driver!.id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['drivers'] });
      toast.success('Driver updated successfully');
      reset();
      onClose();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to update driver');
    },
  });

  const onSubmit = (data: DriverFormData) => {
    if (isEdit) {
      updateMutation.mutate(data);
    } else {
      createMutation.mutate(data);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isEdit ? 'Edit Driver' : 'Add New Driver'}
      size="lg"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit(onSubmit)}
            loading={createMutation.isPending || updateMutation.isPending}
          >
            {isEdit ? 'Update' : 'Create'}
          </Button>
        </>
      }
    >
      <form className="space-y-4">
        {!isEdit && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800">
            <p className="font-medium mb-1">Automatic User Account Creation</p>
            <p>A user account will be automatically created with:</p>
            <ul className="list-disc list-inside mt-1 space-y-0.5">
              <li>Username: License Number</li>
              <li>Password: Transport@2026 (must be changed on first login)</li>
            </ul>
          </div>
        )}
        <Input
          label="Full Name"
          placeholder="e.g., John Mwangi"
          error={errors.fullName?.message}
          {...register('fullName')}
          required
        />

        <Input
          label="License Number"
          placeholder="e.g., DL123456"
          error={errors.licenseNumber?.message}
          {...register('licenseNumber')}
          required
        />

        <div className="grid grid-cols-2 gap-4">
          <Input
            label="Phone Number"
            placeholder="e.g., +255712345678"
            error={errors.phoneNumber?.message}
            {...register('phoneNumber')}
            required
          />
          <Input
            label="Email"
            type="email"
            placeholder="e.g., john@example.com"
            error={errors.email?.message as string}
            {...register('email')}
          />
        </div>
      </form>
    </Modal>
  );
};
